"use client";

import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import OtpInput from "react-otp-input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  authenticateOtp,
  clearError,
  clearSuccess,
  sendOtp,
  setError,
} from "../../features/auth/authSlice";
import Colors from "../../utility/Colors";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { background_orange_gradient } from "../../assets/jss/material";
import constants from "../../common/constants";
import { getSession, setLoginDetails } from "../../utility/SessionManager";
import law from "../../assets/images/law.jpg";
import { keyframes } from "@emotion/react";
import SnackbarComponent from "../../components/snackbar/index.tsx";
import heroBanner from "../../assets/images/hero_section_logo.png";
import CommonTabs from "../../components/tabs/commonTabs.js";
import { display, Grid } from "@mui/system";
import { faAlignJustify } from "@fortawesome/free-solid-svg-icons";
import MobileLogin from "./MobileLogin.js";
import AadhaarLogin from "./AadhaarLogin.js";
import AbhaLogin from "./AbhaLogin.js";
import { resetMobileOtp, resetVerifyOtp } from "./reducer/slice.js";
import {
  resetEnrolByAadhar,
  resetOtpAadhar,
} from "../Register/reducer/abhaAadhar/slice.js";
import AbhaAddressLogin from "./AbhaAddressLogin.js";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tabData = [
    {
      label: "Mobile Number",
      value: "1",
      content: <MobileLogin />,
    },
    {
      label: "Aadhaar Number",
      value: "2",
      content: <AadhaarLogin />,
    },
    { label: "ABHA Number", value: "3", content: <AbhaLogin /> },
    { label: "ABHA Address", value: "4", content: <AbhaAddressLogin /> },
  ];

  const { loading, error, success } = useSelector((state) => state.auth);
  const [showOTP, setShowOTP] = useState(false);
  const [OTP, setOTP] = useState("");
  const [email, setEmail] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpValidity, setOtpValidity] = useState(120);
  const [snackbarOpen, setSnackbarOpen] = useState({
    open: false,
    severity: "",
    message: "",
  });
  const timerRef = useRef(null);

  const handleClick = () => {
    setSnackbarOpen({ open: false, severity: "", message: "" });
  };

  useEffect(() => {
    if (error) {
      setSnackbarOpen({
        open: true,
        severity: "error",
        message: error,
      });
      setTimeout(() => dispatch(clearError()), 5000); // Clear error after 5 seconds
    } else if (success) {
      setSnackbarOpen({
        open: true,
        severity: "success",
        message: success,
      });
      setTimeout(() => dispatch(clearSuccess()), 5000); // Clear success after 5 seconds
    }
  }, [error, success, dispatch]);

  // useEffect(() => {
  //   if (getSession(window, constants.SS_KEY_GROUP_ACCESS_TOKEN)) {
  //     navigate(constants.PATH_DASHBOARD);
  //   }
  // }, [navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    //!to reset next button redirection functionality
    return () => {
      dispatch(resetVerifyOtp());
      dispatch(resetMobileOtp());
      dispatch(resetEnrolByAadhar());
      dispatch(resetOtpAadhar());
    };
  }, []);

  useEffect(() => {
    const token = getSession(window, constants.SS_KEY_GROUP_ACCESS_TOKEN);
    if (token && typeof token === "string" && token.length > 10) {
      navigate(constants.PATH_DASHBOARD);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startOtpTimer = (duration) => {
    let countdown = duration;
    setOtpTimer(countdown);

    timerRef.current = setInterval(() => {
      countdown -= 1;
      if (countdown > 0) {
        setOtpTimer(countdown);
      } else {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setOtpTimer(0);
      }
    }, 1000);
  };

  const onSendOTPClickHandler = async () => {
    if (!email) {
      dispatch(clearError());
      return;
    }

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailValid) {
      dispatch(setError({ message: "Invalid Email Id" }));
      return;
    }

    const result = await dispatch(sendOtp(email));
    if (!result.error) {
      setOtpValidity(120); // Reset validity to 120 seconds
      setShowOTP(true);
      startOtpTimer(120); // This will now start from 120
    }
  };

  const onSubmitClickHandler = async () => {
    if (OTP === "") {
      dispatch(clearError());
      return;
    }
    const resp = await dispatch(authenticateOtp({ emailId: email, otp: OTP }));
    if (!resp?.payload?.error) {
      setOtpValidity(0);
      setLoginDetails(resp.payload.response);
      navigate(constants.PATH_DASHBOARD);
    }
  };

  const onTextChange = (e) => {
    setEmail(e.target.value);
    dispatch(clearError());
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form default behavior
      onSendOTPClickHandler();
    }
  };

  const handleKeyDownSubmitOtp = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form default behavior
      onSubmitClickHandler();
    }
  };

  const changeEmailId = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setOtpValidity(0);
    setShowOTP(false);
    setOTP("");
    setOtpTimer(0);
  };

  return (
    <Card
      sx={{
        width: "100%",
        marginTop: "10px",
        minHeight: "100vh",
        borderRadius: "10px",
        p: 2,
        boxShadow: `0 2px 5px ${Colors.DARK_GREY}`,
        "&:hover": {
          boxShadow: "#008080 0px 2px 5px",
        },
      }}
    >
      <Grid sx={{ mx: 5, my: 5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", my: 4 }}>
          <Typography variant="h5" fontWeight={"bold"}>
            Login To Your ABHA
          </Typography>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              borderRadius: "5px",
              background: "rgb(245, 132, 31)",
              font: "bold",
            }}
            onClick={() => {
              navigate("/register");
            }}
          >
            Create ABHA Number{" "}
          </Button>
        </Box>
        <CommonTabs tabs={tabData} defaultValue="1" />
      </Grid>

      {/* Snackbar for notifications */}
      {
        <SnackbarComponent
          message={snackbarOpen?.message}
          isOpen={snackbarOpen?.open}
          purpose={snackbarOpen?.severity}
          handleClick={handleClick}
        />
      }
    </Card>
  );
};

export default Login;
